import { __awaiter } from "tslib";
import { OptimizelyError } from "../error/optimizly_error";
import { EVENT_STORE_FULL } from "error_message";
import { AsyncPrefixStore, AsyncStoreWithBatchedGet, SyncPrefixStore } from "../utils/cache/store";
import { SerialRunner } from "../utils/executor/serial_runner";
const identity = (v) => v;
const LOGGER_NAME = 'EventStore';
export const DEFAULT_MAX_EVENTS_IN_STORE = 500;
export const DEFAULT_STORE_TTL = 10 * 24 * 60 * 60 * 1000; // 10 days
export const EVENT_STORE_PREFIX = 'optly_event:';
export class EventStore extends AsyncStoreWithBatchedGet {
    constructor(config) {
        super();
        this.operation = 'async';
        this.serializer = new SerialRunner();
        const { maxSize, ttl, store, logger } = config;
        if (store.operation === 'sync') {
            this.store = new SyncPrefixStore(store, EVENT_STORE_PREFIX, identity, identity);
        }
        else {
            this.store = new AsyncPrefixStore(store, EVENT_STORE_PREFIX, identity, identity);
        }
        if (logger) {
            logger.setName(LOGGER_NAME);
            this.logger = logger;
        }
        this.maxSize = maxSize || DEFAULT_MAX_EVENTS_IN_STORE;
        this.ttl = ttl || DEFAULT_STORE_TTL;
    }
    readKeys() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.serializer.run(() => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (this.keys !== undefined) {
                    return;
                }
                try {
                    this.keys = new Set(yield this.getKeys());
                }
                catch (err) {
                    (_a = this.logger) === null || _a === void 0 ? void 0 : _a.error(err);
                }
            }));
        });
    }
    set(key, event) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            yield this.readKeys();
            // readKeys might have failed, in that case we cannot enforce max size
            // that means, the store might grow beyond max size in failure scenarios
            if (this.keys !== undefined && this.keys.size >= this.maxSize) {
                return Promise.reject(new OptimizelyError(EVENT_STORE_FULL, event.event.uuid));
            }
            // this.store.set() might fail and cause the in memory set of keys to 
            // diverge from the actual stored key list. But the in memory set needs
            // to be updated before the store set to limit the eventCount
            // when concurrent set are present. Even if the store set fails, it will 
            // still keep the stored event count below maxSize (it will underfill the store).
            // next getKeys() should fix the discrepency. 
            (_a = this.keys) === null || _a === void 0 ? void 0 : _a.add(key);
            return this.store.set(key, Object.assign(Object.assign({}, event), { _time: { storedAt: Date.now(), ttl: this.ttl } }));
        });
    }
    processStoredEvent(key, value) {
        if (!value)
            return undefined;
        // if there is events in the stored saved by old version of the sdk, 
        // they will not have the storedAt time, update them with the current time
        // before returning
        if (value._time === undefined) {
            value._time = { storedAt: Date.now(), ttl: this.ttl };
            this.set(key, value).catch(() => { });
            return value;
        }
        // use the ttl of the current store even if the stored event has a different ttl
        // this ensures that if the store ttl is reduced, old events will also expire sooner
        // and if the store ttl is increased, old events will stay longer
        // the ttl at the time of save is still stored with the event for potential future use
        if (value._time.storedAt + this.ttl <= Date.now()) {
            this.remove(key).catch(() => { });
            return undefined;
        }
        return value;
    }
    get(key) {
        return __awaiter(this, void 0, void 0, function* () {
            const value = yield this.store.get(key);
            return this.processStoredEvent(key, value);
        });
    }
    remove(key) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            yield this.store.remove(key);
            (_a = this.keys) === null || _a === void 0 ? void 0 : _a.delete(key);
            return;
        });
    }
    getKeys() {
        return __awaiter(this, void 0, void 0, function* () {
            const keys = yield this.store.getKeys();
            this.keys = new Set(keys);
            return keys;
        });
    }
    getBatched(keys) {
        return __awaiter(this, void 0, void 0, function* () {
            const values = yield this.store.getBatched(keys);
            return values.map((value, index) => this.processStoredEvent(keys[index], value));
        });
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=event_store.js.map